import { useCallback } from "react";

import { getBackend as getBackend2 } from "../backend/Backend2";
import type { IGetJobStatusRes } from "../backend/common.generated.types";
import { JOB_STATUS } from "../CommonConsts.generated";
import type { JobStatusEnum } from "../CommonConsts.generated";

import { useAsyncPolling, type AsyncPollDecision } from "./useAsyncPolling";

const DEFAULT_POLL_INTERVAL_MS = 1000;
const DEFAULT_MAX_POLL_ATTEMPTS = 100;

export type UseJobStatusPollingOptions = {
    onCompleted: () => void | Promise<void>;
};

export type UseJobStatusPollingResult = {
    startPolling: (jobId: string) => void;
    isPolling: boolean;
    error: string | null;
};

export interface IGetJobStatusResFixedStatus extends IGetJobStatusRes {
    status: JobStatusEnum;
}

/**
 * Polls `getJobStatus` until the job is created (runs `onCompleted`), errored, ended, or max attempts.
 */
export function useJobStatusPolling(options: UseJobStatusPollingOptions): UseJobStatusPollingResult {
    const { onCompleted } = options;

    const fetchJobStatus = useCallback((jobId: string) => getBackend2().common.getJobStatus({ id: jobId }), []);

    const decide = useCallback(
        (res: IGetJobStatusResFixedStatus): AsyncPollDecision => {
            if (!res) {
                console.log("Job with given uuid not found");
                return { kind: "fail", message: "common.failed" };
            }

            switch (res.status) {
                case JOB_STATUS.ended:
                    return { kind: "complete", run: onCompleted };
                case JOB_STATUS.errored:
                    console.log(res.status_msg || "Job failed");
                    return { kind: "fail", message: "common.failed" };
                default:
                    return { kind: "continue" };
            }
        },
        [onCompleted]
    );

    return useAsyncPolling<IGetJobStatusResFixedStatus>({
        fetchPoll: fetchJobStatus,
        decide,
        pollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
        maxAttempts: DEFAULT_MAX_POLL_ATTEMPTS,
        timeoutMessage: "Job status polling timed out",
    });
}
